import React from 'react';
import { Clock, Zap, Wifi, Armchair, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const Features = () => {
  const features = [
    {
      id: 1,
      title: "24/7 Service",
      description: "Open 365 days a year for your convenience",
      icon: Clock,
      image: "https://images.unsplash.com/photo-1604335399105-a0c585fd81a1?w=800&q=80"
    },
    {
      id: 2,
      title: "High Performance Machines",
      description: "Commercial-grade Dexter front load washers & dryers for large loads",
      icon: Zap,
      image: "https://images.unsplash.com/photo-1674471361352-a1977cf3f18e?w=800&q=80"
    },
    {
      id: 3,
      title: "Industrial Capacity",
      description: "Handle large commercial volumes with professional equipment",
      icon: Armchair,
      image: "https://images.unsplash.com/photo-1765021690100-db6d6a28c1d1?w=800&q=80"
    },
    {
      id: 4,
      title: "Professional Service",
      description: "Expert care with state-of-the-art laundry technology",
      icon: CheckCircle2,
      image: "https://images.unsplash.com/photo-1765021690882-db66fdf47286?w=800&q=80"
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Commercial-Grade Equipment
          </h2>
          <p className="text-lg text-gray-600">
            Professional Dexter washers and dryers delivering superior cleaning results
          </p>
        </div>

        {/* Features grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {features.map((feature) => {
            const Icon = feature.icon;
            
            return (
              <Card 
                key={feature.id}
                className="overflow-hidden border-2 hover:border-teal-300 transition-all duration-300 hover:shadow-xl group"
              >
                {/* Image */}
                <div className="h-48 overflow-hidden">
                  <img 
                    src={feature.image} 
                    alt={feature.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                
                <CardHeader>
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center mb-3">
                    <Icon className="text-white" size={24} />
                  </div>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                </CardHeader>
                
                <CardContent>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Equipment showcase */}
        <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-3xl p-8 sm:p-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                State-of-the-Art Dexter Equipment
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                We use commercial-grade Dexter front-load washers and dryers, the same equipment trusted by 
                professional laundromats and hotels worldwide. Our high-capacity machines ensure your laundry 
                receives the best care possible.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-teal-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">High-efficiency front-load technology</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-teal-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Energy-saving and eco-friendly operation</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-teal-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Gentle on fabrics, tough on stains</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-teal-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Professional-grade cleaning results</span>
                </li>
              </ul>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src="https://images.pexels.com/photos/8774651/pexels-photo-8774651.jpeg?w=400&q=80" 
                  alt="Commercial washing machine"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src="https://images.pexels.com/photos/8774517/pexels-photo-8774517.jpeg?w=400&q=80" 
                  alt="Professional laundry facility"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
